import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, MapPin, Users, Trophy, Target } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MeetCoachesPage = () => {
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async (retryCount = 0) => {
    try {
      setError(false);
      if (retryCount > 0) setRetrying(true);
      
      console.log('Fetching coaches from:', `${API_URL}/api/coaches`);
      const response = await axios.get(`${API_URL}/api/coaches`, { timeout: 15000 });
      console.log('Coaches response:', response.data);
      
      const coachList = response.data?.coaches || [];
      setCoaches(coachList);
      
      if (coachList.length === 0) {
        console.warn('No coaches returned from API');
      }
    } catch (err) {
      console.error('Failed to fetch coaches:', err);
      console.error('Error details:', err.response?.data || err.message);
      
      // Retry up to 3 times on failure
      if (retryCount < 3) {
        console.log(`Retrying... attempt ${retryCount + 1}/3`);
        setTimeout(() => fetchCoaches(retryCount + 1), 1500);
        return;
      }
      setError(true);
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight uppercase text-foreground leading-none mb-6">
            Meet Our Coaches
          </h1>
          <p className="font-serif text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-8">
            Learn from professionals who've competed at the highest levels. Our coaches bring real-world experience and a passion for helping ropers improve at every stage.
          </p>
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Trophy className="w-5 h-5 text-primary" />
              <span>Championship Experience</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="w-5 h-5 text-secondary" />
              <span>Personalized Feedback</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Target className="w-5 h-5 text-accent" />
              <span>All Skill Levels</span>
            </div>
          </div>
        </div>
      </section>

      {/* Coaches Grid */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-muted-foreground">
                {retrying ? 'Retrying connection...' : 'Loading coaches...'}
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-display text-xl uppercase mb-2">Unable to Load Coaches</h3>
              <p className="text-muted-foreground mb-4">Please check your connection and try again.</p>
              <Button onClick={() => { setLoading(true); fetchCoaches(); }} variant="outline">
                Try Again
              </Button>
            </div>
          ) : coaches.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-display text-xl uppercase mb-2">Coaches Coming Soon</h3>
              <p className="text-muted-foreground">We're building our coaching team. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coaches.map(coach => (
                <Card 
                  key={coach.id} 
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1" 
                  data-testid={`coach-card-${coach.id}`}
                  onClick={() => navigate(`/coaches/${coach.id}`)}
                >
                  <div className="aspect-square bg-gradient-to-br from-primary/20 to-secondary/20 relative">
                    {coach.photo_url ? (
                      <img 
                        src={coach.photo_url} 
                        alt={coach.name}
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: coach.photo_position?.objectPosition || 'center'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="font-display font-bold text-8xl text-primary/30">
                          {coach.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    {coach.region && (
                      <div className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-primary" />
                        <span className="text-xs font-medium">{coach.region}</span>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-display font-bold text-2xl uppercase tracking-tight mb-2">
                      {coach.name}
                    </h3>
                    {coach.disciplines?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {coach.disciplines.map(d => (
                          <Badge key={d} variant="secondary" className="text-xs">
                            {d}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {coach.achievements?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {coach.achievements.map((achievement, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-700">
                            <Trophy className="w-3 h-3 mr-1" />
                            {achievement}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {coach.bio && (
                      <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                        {coach.bio}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-12 text-center">
            How Coaching Works
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <span className="font-display font-bold text-2xl text-primary">1</span>
              </div>
              <h3 className="font-display font-bold text-lg uppercase mb-2">Submit Your Video</h3>
              <p className="text-muted-foreground text-sm">
                Share a link to your roping video (YouTube, Google Drive, etc.) and choose your coach.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                <span className="font-display font-bold text-2xl text-secondary">2</span>
              </div>
              <h3 className="font-display font-bold text-lg uppercase mb-2">Get Expert Feedback</h3>
              <p className="text-muted-foreground text-sm">
                Your coach reviews your run and provides detailed, personalized feedback.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <span className="font-display font-bold text-2xl text-accent">3</span>
              </div>
              <h3 className="font-display font-bold text-lg uppercase mb-2">Improve & Repeat</h3>
              <p className="text-muted-foreground text-sm">
                Apply the feedback, practice, and submit again to track your progress.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Want to Coach? */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase mb-4">
            Want to Become a Coach?
          </h2>
          <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
            If you have competitive experience and a passion for teaching, we'd love to hear from you. 
            Join our team and help ropers improve.
          </p>
          <Link to="/apply-coach">
            <Button 
              size="lg" 
              variant="secondary"
              className="rounded-full px-10 py-6 font-bold uppercase tracking-wider"
              data-testid="apply-coach-btn"
            >
              Apply to Coach
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Sign up today and access the full coaching platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-10 py-6 font-bold uppercase tracking-wider"
                data-testid="get-started-btn"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/#pricing">
              <Button 
                variant="outline"
                size="lg" 
                className="rounded-full px-10 py-6 font-bold uppercase tracking-wide border-2"
              >
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" 
                alt="Elevated Roping Media" 
                className="h-8 w-auto"
              />
              <span className="font-display font-bold tracking-tight text-background/90">ELEVATED ROPING MEDIA</span>
            </Link>
            <p className="text-background/60 text-xs">
              © {new Date().getFullYear()} Elevated Roping Media Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MeetCoachesPage;
